import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
} from '@angular/core'

import { Store } from '@ngrx/store'

import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { AiButtonComponent } from '@flowx/designer/modules/ai/modules/external/components/ai-action-button/ai-action-button.component'
import { AiAction } from '@flowx/designer/modules/ai/modules/external/components/ai-action-button/ai-action-button.types'
import { AiPendingMessageComponent } from '@flowx/designer/modules/ai/modules/external/components/ai-pending-message/ai-pending-message.component'
import { AiActions } from '@flowx/designer/modules/ai/store/ai.actions'
import { ChatMessage, FeedbackType } from '@flowx/designer/modules/ai/types/ai.types'

import { AiChatBubbleComponent } from '../ai-chat-bubble/ai-chat-bubble.component'

@Component({
  selector: 'flx-ai-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss'],
  imports: [AiChatBubbleComponent, AiButtonComponent, AiPendingMessageComponent],
})
export class AiChatHistoryComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly #SCROLL_THRESHOLD = 15
  #store = inject(Store)
  #resizeObserver: ResizeObserver | null = null
  #scrollListener: (() => void) | null = null
  #wasScrolledToBottom = true

  @Input() messages: any[] = []
  @Input() loading = false
  @ViewChild('scrollable') scrollable: ElementRef<HTMLDivElement>

  aiKeepAction: AiAction = {
    label: 'Keep',
    flavor: 'accept',
    disabled: false,
    icon: 'actions-keep',
    onAction: (): void => {
      this.#store.dispatch(ConversationActions.keepOutcomeClick())
    },
  }

  aiDiscardAction: AiAction = {
    label: 'Discard',
    flavor: 'discard',
    disabled: false,
    icon: 'actions-delete',
    onAction: (): void => {
      this.#store.dispatch(ConversationActions.discardOutcomeClick())
    },
  }

  keepDiscardOutcomeState = this.#store.selectSignal(ConversationSelectors.keepDiscardOutcomeState)
  activeAgentMainOutcomeFinished = this.#store.selectSignal(
    ConversationSelectors.activeAgentMainOutcomeFinished
  )
  isUiOutcomeFinished = this.#store.selectSignal(ConversationSelectors.isUiOutcomeFinished)
  dataModelChanges = this.#store.selectSignal(ConversationSelectors.aiGeneratedUiDataModelChanges)
  activeAgentThreadExists = this.#store.selectSignal(ConversationSelectors.activeAgentThreadExists)
  aiConversationStreaming = this.#store.selectSignal(ConversationSelectors.loading)

  showAiActionButtons = computed(() => {
    const keepDiscardOutcomeState = this.keepDiscardOutcomeState()
    const activeAgentMainOutcomeFinished = this.activeAgentMainOutcomeFinished()

    if (keepDiscardOutcomeState !== 'idle') {
      return false
    }

    if (activeAgentMainOutcomeFinished && !this.aiConversationStreaming()) {
      return true
    }

    // Designer two-step flow: UI and DM may share the same outcome.
    // After keeping UI, the shared outcome is KEPT, but DM still needs keep/discard.
    // The thread remains active (not finalized) until the DM step is completed.
    const isUiOutcomeFinished = this.isUiOutcomeFinished()
    const dataModelChanges = this.dataModelChanges()
    const activeAgentThreadExists = this.activeAgentThreadExists()
    return isUiOutcomeFinished && !!dataModelChanges && activeAgentThreadExists
  })

  aiDraftActions: AiAction[] = [this.aiKeepAction, this.aiDiscardAction]

  agentProcessingMessage = this.#store.selectSignal(ConversationSelectors.agentProcessingMessage)
  isLoading = this.#store.selectSignal(ConversationSelectors.loading)
  activeNonDefaultThreadId = this.#store.selectSignal(
    ConversationSelectors.activeNonDefaultThreadId
  )
  showPendingMessage = computed(() => this.isLoading() || this.agentProcessingMessage())

  pendingRequestStaticMessage = 'Working on your request.'

  ngAfterViewInit(): void {
    this.scrollToBottom()
    this.#setupResizeObserver()
    this.#setupScrollListener()
  }

  ngOnDestroy(): void {
    this.#resizeObserver?.disconnect()
    if (this.#scrollListener && this.scrollable) {
      this.scrollable.nativeElement.removeEventListener('scroll', this.#scrollListener)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.messages) {
      this.scrollToBottom()
    }
  }

  onFeedback(message: ChatMessage, feedbackType: FeedbackType): void {
    if (!message.id) {
      return
    }
    this.#store.dispatch(AiActions.feedbackClick({ requestId: message.id, feedbackType }))
  }

  cancelPendingRequest(): void {
    if (this.activeNonDefaultThreadId()) {
      this.#store.dispatch(ConversationActions.discardThreadClick({}))
    } else {
      this.#store.dispatch(ConversationActions.cancelIntentUnderstandingClick())
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.scrollable) {
        this.scrollable.nativeElement.scrollTop = this.scrollable.nativeElement.scrollHeight
      }
    }, 0)
  }

  private isScrolledToBottom(): boolean {
    if (!this.scrollable) {
      return false
    }
    const element = this.scrollable.nativeElement
    return element.scrollHeight - element.scrollTop - element.clientHeight <= this.#SCROLL_THRESHOLD
  }

  #setupScrollListener(): void {
    if (!this.scrollable) {
      return
    }

    this.#scrollListener = () => {
      this.#wasScrolledToBottom = this.isScrolledToBottom()
    }
    this.scrollable.nativeElement.addEventListener('scroll', this.#scrollListener)
  }

  #setupResizeObserver(): void {
    if (!this.scrollable) {
      return
    }

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#wasScrolledToBottom) {
        this.scrollToBottom()
      }
    })

    this.#resizeObserver.observe(this.scrollable.nativeElement)
  }
}
